import React, { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Spinner, makeStyles, tokens } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { useAtomValue, useSetAtom } from "jotai";
import { isRTLAtom, cultureAtom, isAuthenticatedAtom } from "./store";
import { authLoadingAtom } from "./store/atoms/authAtoms";
import { ThemeProviderWrapper } from "./styles/ThemeProviderWrapper";
import { AuthProvider } from "./auth/AuthProvider";
import { AuthenticatedRegistrationRoute } from "./components/AuthenticatedRegistrationRoute";

const Login = React.lazy(() => import("./pages/auth/Login"));
const AuthCallback = React.lazy(() => import("./auth/AuthCallback"));
const Register = React.lazy(() => import("./pages/registration/Register"));
const PendingApproval = React.lazy(() => import("./pages/registration/PendingApproval"));
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const Profile = React.lazy(() => import("./pages/Profile"));

const MyEntityLinks = React.lazy(() => import("./pages/entities/MyEntityLinks"));
const MessagesPage = React.lazy(() => import("./pages/messages/MessagesPage"));
const EntitySearch = React.lazy(() => import("./pages/entities/EntitySearch"));
const PendingInvitations = React.lazy(() => import("./pages/entities/PendingInvitations"));
const Layout = React.lazy(() => import("./layouts/Layout"));

const useLayoutStyles = makeStyles({
  app: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    width: "100%",
    overflowX: "hidden",
    overflowY: "auto",
    backgroundColor: tokens.colorNeutralBackground3,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    backgroundColor: tokens.colorNeutralBackground1,
  },
});

const LoadingFallback = () => {
  const layoutStyles = useLayoutStyles();
  // useSuspense: false prevents an infinite loop when this component is itself a Suspense fallback
  const { t } = useTranslation("agentportal-common", { useSuspense: false });
  return (
    <div className={layoutStyles.loadingContainer}>
      <Spinner size="extra-large" label={t("loading", "Loading...")} />
    </div>
  );
};

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);
  const isAuthLoading = useAtomValue(authLoadingAtom);

  if (isAuthLoading) {
    return <LoadingFallback />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

function App() {
  const isRTL = useAtomValue(isRTLAtom);
  const setCulture = useSetAtom(cultureAtom);
  const { i18n } = useTranslation();
  const layoutStyles = useLayoutStyles();

  React.useEffect(() => {
    if (i18n.language) {
      setCulture(i18n.language);
    }

    const handleLanguageChange = (lng: string) => {
      setCulture(lng);
    };

    i18n.on("languageChanged", handleLanguageChange);

    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [i18n, setCulture]);

  React.useEffect(() => {
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
    document.documentElement.lang = i18n.language;
  }, [isRTL, i18n.language]);

  return (
    <Suspense fallback={<LoadingFallback />}>
      <ThemeProviderWrapper>
        <AuthProvider>
          <BrowserRouter>
            <div className={layoutStyles.app}>
              <Routes>
                <Route path="/" element={<Navigate to="/login" replace />} />
                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <Login />
                    </PublicRoute>
                  }
                />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route
                  path="/register"
                  element={
                    <AuthenticatedRegistrationRoute>
                      <Register />
                    </AuthenticatedRegistrationRoute>
                  }
                />
                <Route path="/pending" element={<PendingApproval />} />

                <Route
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/entities" element={<MyEntityLinks />} />
                  <Route path="/entities/search" element={<EntitySearch />} />
                  <Route path="/entities/invitations" element={<PendingInvitations />} />
                  <Route path="/messages" element={<MessagesPage />} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProviderWrapper>
    </Suspense>
  );
}

export default App;
